/**
 * QuickCompareZone - Zona de drop flotante para comparación rápida
 * Aparece cuando se arrastra un ticker y permite soltar para comparar
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { logger } from '../utils/logger';
import { createPortal } from 'react-dom';
import { 
  Scales, X, Plus, TrendUp, TrendDown, ChartLine, Trash,
  ArrowsOutSimple, Lightning
} from '@phosphor-icons/react';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip } from 'recharts';

const API = process.env.REACT_APP_BACKEND_URL;

// Colores para los activos
const COLORS = ['#00dcff', '#22c55e', '#f59e0b', '#ef4444', '#a855f7'];

// Estilos constantes para evitar re-renders (fuera del componente)
const AXIS_TICK_STYLE = { fontSize: 9, fill: '#666' };
const TOOLTIP_STYLE = { 
  backgroundColor: '#1a1a2e', 
  border: '1px solid #333',
  borderRadius: '8px',
  fontSize: '10px'
};

// Componente principal
const QuickCompareZone = ({ onOpenFullComparator }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [tickers, setTickers] = useState([]);
  const [assetsData, setAssetsData] = useState({});
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(false);

  // Escuchar eventos de drag globales
  useEffect(() => {
    const handleDragStart = (e) => {
      if (e.dataTransfer?.types?.includes('text/ticker')) {
        setIsDragging(true);
      }
    };

    const handleDragEnd = () => {
      setIsDragging(false);
    };

    // Escuchar evento custom de drag
    const handleTickerDrag = (e) => {
      setIsDragging(true);
    };

    const handleTickerDragEnd = () => {
      setIsDragging(false);
    };

    document.addEventListener('dragstart', handleDragStart);
    document.addEventListener('dragend', handleDragEnd);
    window.addEventListener('ticker-drag-start', handleTickerDrag);
    window.addEventListener('ticker-drag-end', handleTickerDragEnd);

    return () => {
      document.removeEventListener('dragstart', handleDragStart);
      document.removeEventListener('dragend', handleDragEnd);
      window.removeEventListener('ticker-drag-start', handleTickerDrag);
      window.removeEventListener('ticker-drag-end', handleTickerDragEnd);
    };
  }, []);

  // Fetch data para un ticker
  const fetchTickerData = useCallback(async (ticker) => {
    try {
      // Obtener info y historial
      const [infoRes, histRes] = await Promise.all([
        fetch(`${API}/api/stock/${ticker}/info`),
        fetch(`${API}/api/stock/${ticker}/history?period=1mo&interval=1d`)
      ]);
      
      const info = await infoRes.json();
      const hist = await histRes.json();
      
      return {
        ticker,
        info,
        history: hist.data || hist.history || []
      };
    } catch (error) {
      logger.error(`Error fetching ${ticker}:`, error);
      return null;
    }
  }, []);

  // Añadir ticker
  const addTicker = useCallback(async (ticker) => {
    const tickerUpper = ticker.toUpperCase().trim();
    if (!tickerUpper || tickers.includes(tickerUpper) || tickers.length >= 5) return;
    
    setLoading(true);
    const data = await fetchTickerData(tickerUpper);
    
    if (data) {
      setTickers(prev => [...prev, tickerUpper]);
      setAssetsData(prev => ({ ...prev, [tickerUpper]: data }));
      setIsExpanded(true);
    }
    setLoading(false);
  }, [tickers, fetchTickerData]);

  // Eliminar ticker
  const removeTicker = (ticker) => {
    setTickers(prev => prev.filter(t => t !== ticker));
    setAssetsData(prev => {
      const newData = { ...prev };
      delete newData[ticker];
      return newData;
    });
  };

  // Limpiar todo
  const clearAll = () => {
    setTickers([]);
    setAssetsData({});
    setChartData([]);
    setIsExpanded(false);
  };

  // Calcular datos del gráfico cuando cambian los activos
  useEffect(() => {
    if (tickers.length === 0) {
      setChartData([]);
      return;
    }

    // Encontrar el histórico más largo como referencia
    let maxLength = 0;
    let referenceHistory = [];
    
    tickers.forEach(ticker => {
      const history = assetsData[ticker]?.history || [];
      if (history.length > maxLength) {
        maxLength = history.length;
        referenceHistory = history;
      }
    });

    if (referenceHistory.length === 0) return;

    // Calcular valores base para normalización
    const baseValues = {};
    tickers.forEach(ticker => {
      const history = assetsData[ticker]?.history || [];
      if (history.length > 0) {
        baseValues[ticker] = history[0].close;
      }
    });

    // Crear datos normalizados
    const normalized = referenceHistory.map((item, idx) => {
      const point = {
        date: new Date(item.date).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' })
      };

      tickers.forEach(ticker => {
        const history = assetsData[ticker]?.history || [];
        if (history[idx] && baseValues[ticker]) {
          point[ticker] = ((history[idx].close / baseValues[ticker]) - 1) * 100;
        }
      });

      return point;
    });

    setChartData(normalized);
  }, [tickers, assetsData]);

  // Manejar drop
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    
    const ticker = e.dataTransfer.getData('text/ticker') || e.dataTransfer.getData('text/plain');
    if (ticker) {
      addTicker(ticker);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  // No renderizar si no hay nada que mostrar
  if (!isDragging && tickers.length === 0 && !isExpanded) {
    return null;
  }

  const content = (
    <div 
      className={`fixed z-[9999] transition-all duration-300 ${
        isExpanded 
          ? 'bottom-4 right-4 w-[500px]' 
          : isDragging 
            ? 'bottom-4 right-4 w-64'
            : 'bottom-4 right-4 w-12'
      }`}
    >
      {/* Zona de Drop cuando se está arrastrando */}
      {isDragging && !isExpanded && (
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          className="bg-gradient-to-br from-gold-500/30 to-zinc-500/30 border-2 border-dashed border-gold-400 rounded-xl p-6 text-center animate-pulse"
        >
          <Scales size={40} className="mx-auto text-gold-400 mb-2" />
          <p className="text-gold-400 font-bold text-sm">Suelta aquí para comparar</p>
          <p className="text-gold-400/60 text-xs mt-1">Máximo 5 activos</p>
        </div>
      )}

      {/* Mini badge cuando hay tickers pero no está expandido */}
      {!isDragging && tickers.length > 0 && !isExpanded && (
        <button
          onClick={() => setIsExpanded(true)}
          className="w-12 h-12 rounded-full bg-gradient-to-br from-gold-500 to-zinc-500 flex items-center justify-center shadow-lg hover:scale-110 transition-transform"
          title="Abrir comparación rápida"
        >
          <span className="text-white font-bold">{tickers.length}</span>
        </button>
      )}

      {/* Panel expandido */}
      {isExpanded && (
        <div 
          className="bg-zinc-900/95 backdrop-blur-xl border border-gold-500/30 rounded-xl shadow-2xl overflow-hidden"
          onDrop={handleDrop}
          onDragOver={handleDragOver}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-3 border-b border-zinc-800 bg-gradient-to-r from-gold-500/10 to-zinc-500/10">
            <div className="flex items-center gap-2">
              <Lightning size={18} className="text-gold-400" />
              <span className="text-sm font-bold text-white">Comparación Rápida</span>
              <span className="text-xs text-zinc-500">({tickers.length}/5)</span>
            </div>
            <div className="flex items-center gap-1">
              {tickers.length >= 2 && onOpenFullComparator && (
                <button
                  onClick={() => onOpenFullComparator(tickers)}
                  className="p-1.5 rounded-lg hover:bg-white/10 text-gold-400 transition-colors"
                  title="Abrir comparador completo"
                >
                  <ArrowsOutSimple size={16} />
                </button>
              )}
              <button
                onClick={clearAll}
                className="p-1.5 rounded-lg hover:bg-red-500/20 text-zinc-400 hover:text-red-400 transition-colors"
                title="Limpiar todo"
              >
                <Trash size={16} />
              </button>
              <button
                onClick={() => setIsExpanded(false)}
                className="p-1.5 rounded-lg hover:bg-white/10 text-zinc-400 transition-colors"
                title="Minimizar"
              >
                <X size={16} />
              </button>
            </div>
          </div>

          {/* Drop zone indicator */}
          {isDragging && (
            <div className="p-2 bg-gold-500/10 border-b border-gold-500/30 text-center">
              <p className="text-gold-400 text-xs flex items-center justify-center gap-2">
                <Plus size={14} /> Suelta para añadir
              </p>
            </div>
          )}

          {/* Tickers */}
          <div className="p-3 flex flex-wrap gap-2">
            {tickers.map((ticker, idx) => {
              const info = assetsData[ticker]?.info || {};
              const change = info.changePercent || info.change || 0;
              const isPositive = change >= 0;
              
              return (
                <div
                  key={ticker}
                  className="flex items-center gap-2 px-2 py-1 rounded-lg text-xs"
                  style={{
                    background: `${COLORS[idx]}15`,
                    border: `1px solid ${COLORS[idx]}40`
                  }}
                >
                  <div 
                    className="w-2 h-2 rounded-full"
                    style={{ background: COLORS[idx] }}
                  />
                  <span className="font-bold text-white">{ticker}</span>
                  <span className={`font-mono ${isPositive ? 'text-green-400' : 'text-red-400'}`}>
                    {isPositive ? '+' : ''}{change.toFixed(2)}%
                  </span>
                  <button
                    onClick={() => removeTicker(ticker)}
                    className="hover:text-red-400 text-zinc-500 transition-colors"
                  >
                    <X size={12} />
                  </button>
                </div>
              );
            })}
            
            {loading && (
              <div className="px-3 py-1 rounded-lg bg-zinc-800 text-zinc-400 text-xs animate-pulse">
                Cargando...
              </div>
            )}
            
            {tickers.length === 0 && !loading && (
              <p className="text-zinc-500 text-xs">
                Arrastra activos aquí o desde cualquier ventana
              </p>
            )}
          </div>

          {/* Mini gráfico */}
          {chartData.length > 0 && tickers.length >= 1 && (
            <div className="px-3 pb-3">
              <div className="h-32 bg-zinc-800/50 rounded-lg p-2">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData}>
                    <XAxis 
                      dataKey="date" 
                      tick={AXIS_TICK_STYLE}
                      tickLine={false}
                      axisLine={false}
                    />
                    <YAxis 
                      tick={AXIS_TICK_STYLE}
                      tickFormatter={(v) => `${v > 0 ? '+' : ''}${v.toFixed(0)}%`}
                      tickLine={false}
                      axisLine={false}
                      width={35}
                    />
                    <Tooltip 
                      contentStyle={TOOLTIP_STYLE}
                      formatter={(value, name) => [`${value > 0 ? '+' : ''}${value.toFixed(2)}%`, name]}
                    />
                    {tickers.map((ticker, idx) => (
                      <Line
                        key={ticker}
                        type="monotone"
                        dataKey={ticker}
                        stroke={COLORS[idx]}
                        strokeWidth={2}
                        dot={false}
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </div>
              
              {/* Resumen rápido */}
              {tickers.length >= 2 && (
                <div className="mt-2 text-[10px] text-zinc-500 text-center">
                  {(() => {
                    const lastPoints = chartData[chartData.length - 1] || {};
                    const values = tickers.map(t => ({ ticker: t, value: lastPoints[t] || 0 }));
                    const best = values.reduce((a, b) => a.value > b.value ? a : b);
                    const worst = values.reduce((a, b) => a.value < b.value ? a : b);
                    
                    return (
                      <>
                        <span className="text-green-400">{best.ticker}</span> mejor rendimiento ({best.value > 0 ? '+' : ''}{best.value.toFixed(1)}%) • 
                        <span className="text-red-400 ml-1">{worst.ticker}</span> peor ({worst.value > 0 ? '+' : ''}{worst.value.toFixed(1)}%)
                      </>
                    );
                  })()}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );

  // Usar portal para renderizar fuera del árbol normal
  return createPortal(content, document.body);
};

export default QuickCompareZone;
